import { useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client/react/hooks';
import { useSession } from './useSession';
import { UPDATE_KITCHEN_DISPLAY } from '../graphql/kitchenDisplay';
import { getAppVersion } from '../utils/getAppVersion';
import { getPlatform } from '../utils/getPlatform';
import { noopHandler } from '../utils/errorHandlers';

export function useAppVersionSync(): void {
  const { session } = useSession();
  const kitchenDisplay = session.kitchenDisplay;
  const kitchenDisplayId = kitchenDisplay?.id;
  const kitchenDisplayName = kitchenDisplay?.name;
  const cachedAppVersion = kitchenDisplay?.appVersion;
  const cachedPlatform = kitchenDisplay?.platform;
  const storeId = kitchenDisplay?.store?.id ?? session.currentStore?.id;

  const [updateKitchenDisplay] = useMutation(UPDATE_KITCHEN_DISPLAY, {
    onError: noopHandler,
    context: { headers: { store: storeId } },
  });

  const syncedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!kitchenDisplayId) return;
    if (!kitchenDisplayName) return;
    const currentVersion = getAppVersion();
    const currentPlatform = getPlatform();
    if (currentVersion === 'unknown') return;
    if (
      cachedAppVersion === currentVersion &&
      cachedPlatform === currentPlatform
    ) {
      return;
    }

    const key = `${kitchenDisplayId}:${currentVersion}:${currentPlatform}`;
    if (syncedKeyRef.current === key) return;
    syncedKeyRef.current = key;

    // Clear the synced key on failure so the next effect run (e.g. when
    // updateKitchenDisplay's reference changes after Apollo updates mutation
    // state) can retry. Without this, a transient error would block resync
    // for the lifetime of the mount.
    void updateKitchenDisplay({
      variables: {
        input: {
          id: kitchenDisplayId,
          name: kitchenDisplayName,
          appVersion: currentVersion,
          platform: currentPlatform,
        },
      },
    }).catch(() => {
      if (syncedKeyRef.current === key) {
        syncedKeyRef.current = null;
      }
    });
  }, [
    kitchenDisplayId,
    kitchenDisplayName,
    cachedAppVersion,
    cachedPlatform,
    updateKitchenDisplay,
  ]);
}
